import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Mail, Send, Copy, CheckCheck, Sprout } from "lucide-react";

const CONTACT_EMAIL = "bahoimatch@gmail.com";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (message.trim().length < 10) {
      setErr("Please write at least a sentence (10+ characters).");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setSent(true);
      toast.success(data.message || "Message sent!");
      setName("");
      setEmail("");
      setMessage("");
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      setErr(msg || "Could not send. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  return (
    <Layout>
      <div data-testid="contact-page" className="max-w-6xl mx-auto px-5 md:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* LEFT: intro + direct email */}
          <div className="md:sticky md:top-24">
            <span className="overline text-[var(--cm-primary)]">Get in touch</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-2 tracking-tight">
              Tell us what you're <span className="text-[var(--cm-primary)]">building</span>.
            </h1>
            <p className="text-ink-soft mt-4 max-w-md leading-relaxed">
              Whether you're a volunteer with a question, an organization wanting to post opportunities,
              or a curious soul who just wants to chat about time banking — we'd love to hear from you.
            </p>

            <div className="mt-10 card-soft p-6 relative overflow-hidden">
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[var(--cm-accent)]/15 blur-2xl" />
              <span className="overline text-ink-soft">Prefer email?</span>
              <div className="mt-3 flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-[var(--cm-primary)]/10 grid place-items-center">
                  <Mail className="h-4 w-4 text-[var(--cm-primary)]" />
                </span>
                <a
                  data-testid="contact-direct-email"
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-display font-semibold text-lg hover:text-[var(--cm-primary)] transition-colors"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <button
                data-testid="contact-copy-email"
                onClick={copyEmail}
                className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-ink-soft hover:text-ink transition-colors"
              >
                {copied ? (
                  <>
                    <CheckCheck className="h-3.5 w-3.5 text-[var(--cm-secondary)]" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Copy address
                  </>
                )}
              </button>
            </div>

            <div className="mt-8 flex items-start gap-3 text-sm text-ink-soft">
              <Sprout className="h-4 w-4 text-[var(--cm-secondary)] mt-0.5 shrink-0" />
              <p>
                Bahoi is a small, intentional team. We read every message and reply within 2–3 days.
                For urgent issues with a shift,{" "}
                <Link to="/organize" className="underline underline-offset-2 hover:text-ink">
                  ping your organizer
                </Link>{" "}
                instead — they verify faster than we can.
              </p>
            </div>
          </div>

          {/* RIGHT: form */}
          <div className="card-soft p-7 md:p-9 relative">
            {sent && (
              <div
                data-testid="contact-success"
                className="absolute inset-0 z-10 bg-surface rounded-2xl flex flex-col items-center justify-center text-center p-10"
              >
                <div className="h-14 w-14 rounded-full bg-[var(--cm-secondary)]/15 grid place-items-center mb-4">
                  <CheckCheck className="h-6 w-6 text-[var(--cm-secondary)]" />
                </div>
                <h3 className="font-display font-bold text-2xl">Message received.</h3>
                <p className="text-ink-soft mt-2 max-w-sm">
                  Thanks for reaching out. We'll reply to your email within 2–3 days.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setSent(false)}
                  className="btn-outline mt-6 text-sm"
                >
                  Send another
                </button>
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-5">
              <Field label="Your name" testid="contact-name-field">
                <input
                  data-testid="contact-name-input"
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-app border border-ink-soft rounded-xl px-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/40"
                  placeholder="Alex Chen"
                />
              </Field>
              <Field label="Your email" testid="contact-email-field">
                <input
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-app border border-ink-soft rounded-xl px-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/40"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Your message" testid="contact-message-field">
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={6}
                  minLength={10}
                  maxLength={4000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-app border border-ink-soft rounded-xl px-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/40 resize-y leading-relaxed"
                  placeholder="What's on your mind?"
                />
                <p className="text-xs text-ink-soft mt-1.5">{message.length}/4000</p>
              </Field>
              {err && (
                <div
                  data-testid="contact-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2.5 rounded-lg"
                >
                  {err}
                </div>
              )}
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Sending…" : (
                  <>
                    Send message <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-ink-soft">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
